import type { TFunction } from 'i18next';
import { garaUrl } from './link-gara';
import { puntiTorneo } from './tournament-helpers';

export type ShareResult = 'shared' | 'copied' | 'failed';

/** Che cosa si manda in giro. */
interface Condivisione {
  title: string;
  text: string;
  url: string;
  /**
   * Cosa finisce negli appunti quando il foglio nativo non c'e'. Senza questo
   * campo e' l'`url` nudo, che e' quello che vuole un INVITO: chi lo incolla in
   * chat scrive la sua frase da se'. Un RISULTATO invece porta anche il testo —
   * il podio — perche' negli appunti il link da solo perderebbe tutto cio' che
   * il foglio nativo avrebbe mostrato accanto.
   */
  appunti?: string;
}

/**
 * Condividere: foglio nativo quando c'e' (su telefono e' la strada di WhatsApp,
 * che e' il motivo per cui questo modulo esiste), appunti altrimenti.
 * Restituisce che cosa e' successo, cosi' chi chiama puo' dare il riscontro a
 * schermo.
 *
 * Era lo stesso blocco copiato sei volte. Non e' stato un problema di righe
 * ripetute: le sei copie avevano smesso di essere identiche — una non aveva la
 * guardia su `navigator`, e la settima (il podio dell'arena, scritta a mano
 * dentro la pagina) non aveva nemmeno il ripiego sugli appunti, cioe' su desktop
 * il suo bottone non faceva NIENTE.
 *
 * Chiudere il foglio nativo non e' un errore, e' un ripensamento: non si segnala
 * niente e si ripiega sugli appunti.
 */
async function condividi({ title, text, url, appunti }: Condivisione): Promise<ShareResult> {
  if (typeof navigator !== 'undefined' && 'share' in navigator) {
    try {
      await navigator.share({ title, text, url });
      return 'shared';
    } catch {
      // Foglio chiuso dall'utente, o fallito: si ripiega sulla copia.
    }
  }

  try {
    await navigator.clipboard.writeText(appunti ?? url);
    return 'copied';
  } catch {
    return 'failed';
  }
}

/**
 * Build the deep-link that opens today's daily puzzle for a given variant.
 * Parsed on app start in App.tsx (`?puzzle=daily&variant=…`).
 */
export function dailyPuzzleUrl(variant: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/?puzzle=daily&variant=${encodeURIComponent(variant.toLowerCase())}`;
}

/**
 * Share the daily puzzle with a friend: native share sheet when available
 * (mobile), otherwise copy the link to the clipboard. Returns what happened so
 * the caller can show inline feedback ("Copied!").
 */
export async function shareDailyPuzzle(variant: string, t: TFunction): Promise<ShareResult> {
  return condividi({
    title: t('puzzle.shareTitle', { defaultValue: 'Daily puzzle' }),
    text: t('puzzle.shareText', {
      defaultValue: 'I solved today’s daily puzzle — can you?',
    }),
    url: dailyPuzzleUrl(variant),
  });
}

/**
 * Build the deep-link that invites a friend to a challenge. The link preview
 * endpoint is public, so the friend can open it logged out (and then play as a
 * guest). Parsed on app start in App.tsx (`?challenge=…`).
 */
export function challengeLinkUrl(id: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/?challenge=${encodeURIComponent(id)}`;
}

/**
 * Share a challenge link: native share sheet when available (on mobile this is
 * the WhatsApp path — the whole point of the feature), clipboard otherwise.
 *
 * @param summary human-readable settings ("Internazionale · 5+3"), appended to
 * the message so the friend knows what they are accepting before tapping.
 */
export async function shareChallengeLink(id: string, t: TFunction, summary?: string): Promise<ShareResult> {
  return condividi({
    title: t('online.challenge.link.shareTitle', { defaultValue: 'Draughts challenge' }),
    text: summary
      ? t('online.challenge.link.shareTextWith', {
          summary,
          defaultValue: `Play me at draughts — {{summary}}`,
        })
      : t('online.challenge.link.shareText', { defaultValue: 'Play me at draughts' }),
    url: challengeLinkUrl(id),
  });
}

/**
 * Build the deep-link that opens the board editor on a given position. The
 * position travels IN the link (there is nothing to fetch: a saved position is
 * private to its owner), so the FEN needs the variant next to it — the same FEN
 * read with the wrong geometry is a different position, or none.
 * Parsed on app start in App.tsx (`?editor=<fen>&variant=…`).
 */
export function editorPositionUrl(fen: string, variant: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/?editor=${encodeURIComponent(fen)}&variant=${encodeURIComponent(variant.toLowerCase())}`;
}

/**
 * Share a composed position: native share sheet when available (mobile),
 * otherwise copy the link to the clipboard. Returns what happened so the caller
 * can show inline feedback ("Link copied").
 */
export async function shareEditorPosition(fen: string, variant: string, t: TFunction): Promise<ShareResult> {
  return condividi({
    title: t('editor.library.shareTitle', { defaultValue: 'Draughts position' }),
    text: t('editor.library.shareText', { defaultValue: 'Take a look at this draughts position' }),
    url: editorPositionUrl(fen, variant),
  });
}

/**
 * Build the deep-link that opens a game's replay. The game-details endpoint is
 * public, so this works for logged-out recipients too. Parsed on app start in
 * App.tsx (`?game=…`).
 *
 * Il link porta la partita e nient'altro. Fino alla migrazione 114 poteva
 * portare anche un gettone che apriva le note di chi condivideva: era una
 * pubblicazione di testo libero verso destinatari indeterminati, ed e' stata
 * ritirata. Le proprie note si portano via dal PDN, che resta in mano a chi
 * lo scarica.
 */
export function gameReplayUrl(gameId: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/?game=${encodeURIComponent(gameId)}`;
}

/**
 * Share a game replay with a friend: native share sheet when available
 * (mobile), otherwise copy the link to the clipboard. Returns what happened so
 * the caller can show inline feedback ("Copied!").
 */
export async function shareGame(gameId: string, t: TFunction): Promise<ShareResult> {
  return condividi({
    title: t('replay.shareTitle', { defaultValue: 'Draughts game' }),
    text: t('replay.shareText', { defaultValue: 'Check out this draughts game' }),
    url: gameReplayUrl(gameId),
  });
}

/**
 * Il link che invita qualcuno a un torneo. Forma: `/?tournament=<id>[&t=<gettone>]`,
 * che e' esattamente cio' che `parseTournamentDeepLink` legge all'avvio — le due
 * meta' stanno in `link-gara.ts`, una accanto all'altra.
 *
 * Il gettone c'e' solo per i tornei privati, ed e' la chiave che fa entrare chi
 * ha il link. Senza gettone la chiave non compare affatto: un `&t=` vuoto
 * arriverebbe dall'altra parte come stringa vuota, cioe' come un gettone
 * sbagliato invece che come nessun gettone.
 */
export function tournamentInviteUrl(id: string, token: string | null): string {
  return invitoUrl('tournament', id, token);
}

/**
 * Il link d'invito, per l'una e per l'altra specie di gara: il link della gara
 * piu' la chiave, quando c'e' una chiave.
 *
 * Il percorso NON si riscrive qui: viene da `garaUrl`, che e' la stessa funzione
 * da cui lo prende la barra degli indirizzi. Scritto due volte, il giorno che
 * una delle due forme cambiasse l'altra resterebbe indietro in silenzio.
 */
export function invitoUrl(genere: 'tournament' | 'arena', id: string, token: string | null): string {
  const gettone = token ? `&t=${encodeURIComponent(token)}` : '';
  return `${garaUrl(genere, id)}${gettone}`;
}

/**
 * Condivide l'invito a un torneo: il link con la chiave dentro, a una persona
 * alla volta. Per mandare in giro un RISULTATO c'e'
 * `condividiClassificaTorneo`, che non porta la chiave.
 */
export async function shareTournamentInvite(
  id: string,
  token: string | null,
  nome: string,
  t: TFunction,
): Promise<ShareResult> {
  return condividi({
    title: t('tournament.share.title', { defaultValue: 'Draughts tournament' }),
    text: t('tournament.share.text', {
      nome,
      defaultValue: `You are invited to the tournament ${nome} on draughtsgame.com`,
    }),
    url: tournamentInviteUrl(id, token),
  });
}

/**
 * Condivide l'invito a un'ARENA. Stessa meccanica della gemella qui sopra, e un
 * parametro diverso nel link, perche' sono due schermate diverse.
 */
export async function shareArenaInvite(
  id: string,
  token: string | null,
  nome: string,
  t: TFunction,
): Promise<ShareResult> {
  return condividi({
    title: t('arena.share.title', { defaultValue: 'Draughts arena' }),
    text: t('arena.share.text', {
      nome,
      defaultValue: `You are invited to the arena ${nome} on draughtsgame.com`,
    }),
    url: invitoUrl('arena', id, token),
  });
}

/* ── La classifica di una gara finita ────────────────────────────────────────
 *
 * Un invito e un risultato sono due messaggi diversi e due link diversi, e la
 * differenza non e' di stile: l'invito porta il gettone, il risultato NO. Un
 * torneo privato finito la cui classifica girasse col gettone in coda
 * regalerebbe la chiave d'ingresso a chiunque la inoltri. Qui non c'e' un
 * parametro da ricordarsi di non passare: non c'e' niente da passare.
 */

/** Una riga di podio: quel poco che serve a scriverla in un messaggio. */
export interface RigaPodio {
  rank: number;
  username: string;
  points: number;
}

/**
 * Le righe del podio, raggruppate per POSIZIONE.
 *
 * Due cose, entrambe sul pari merito. Gli ex aequo stanno su UNA riga («1. anna,
 * bob — 7 pts»), perche' il posto e' uno e scriverlo due volte lo farebbe
 * leggere come due posti. E si prendono le prime tre POSIZIONI, non le prime tre
 * righe: con un pari a tre per il primo posto, tre righe sarebbero tre volte lo
 * stesso posto, e il messaggio direbbe meno di quanto sembra.
 *
 * Il salto che resta dopo un pari — 1, 1, 3 — non e' un buco da chiudere: e'
 * come si numera una classifica, e la stessa che si vede a schermo.
 */
export function righeDelPodio(righe: RigaPodio[], t: TFunction, posizioni = 3): string[] {
  /* Lo stesso ordine di presentazione della tabella (`TournamentStandings`):
     posto, e a parita' il nome. Senza il secondo criterio due ex aequo
     cambierebbero ordine fra una lettura e l'altra. */
  const ordinate = [...righe].sort((a, b) => a.rank - b.rank || a.username.localeCompare(b.username));

  /* I nomi si accumulano, i punti no: a pari posto i punti sono gli stessi (e'
     la definizione di pari posto), quindi il primo che arriva li detta. */
  const perPosto = new Map<number, { nomi: string[]; punti: number }>();
  for (const riga of ordinate) {
    const gruppo = perPosto.get(riga.rank);
    if (gruppo) gruppo.nomi.push(riga.username);
    else perPosto.set(riga.rank, { nomi: [riga.username], punti: riga.points });
  }

  const pts = t('tournament.lobby.pts', { defaultValue: 'pts' });
  return [...perPosto.entries()]
    .slice(0, posizioni)
    /* `puntiTorneo` e non il numero nudo: e' la funzione con cui i punti si
       scrivono in tutta l'app, mezzi punti compresi («7½»). */
    .map(([posto, g]) => `${posto}. ${g.nomi.join(', ')} — ${puntiTorneo(g.punti)} ${pts}`);
}

/**
 * Il messaggio che accompagna la classifica di un torneo finito.
 *
 * Puro e esportato apposta: e' la parte che va provata in otto lingue, e provarla
 * attraverso `navigator.share` vorrebbe dire non provarla.
 *
 * NON parla del vincitore, e non e' una dimenticanza. Il nome sulla fascia d'oro
 * viene dalla colonna `winner_id`, che e' quello che il server ha messo in
 * archivio; la classifica ha sempre un primo, anche quando quella colonna e'
 * vuota (torneo interrotto, o vincitore che ha poi cancellato il conto). Questo
 * testo riferisce la CLASSIFICA e nient'altro: cosi' non puo' incoronare
 * qualcuno che la pagina non incorona.
 */
export function testoClassificaTorneo(nome: string, podio: RigaPodio[], t: TFunction): string {
  const titolo = t('tournament.share.standingsTitle', {
    nome,
    defaultValue: `Final standings — ${nome}`,
  });
  const righe = righeDelPodio(podio, t);
  /* Senza nemmeno una riga si dice la stessa frase che dice la fascia in quel
     caso esatto: messaggio e schermo non devono raccontare due cose. */
  const corpo = righe.length > 0
    ? righe
    : [t('tournament.ended.noWinner', { defaultValue: 'No winner: no games were played.' })];
  return [titolo, ...corpo].join('\n');
}

/** Il proprio risultato in un'arena, piu' il podio. */
export interface RiepilogoArena {
  posto: number | null;
  suQuante: number;
  punti: number;
  partite: number;
  percentuale: number;
  podio: RigaPodio[];
}

/**
 * Il messaggio del podio di un'arena: il proprio risultato — che e' il motivo
 * per cui si condivide da quella schermata — e sotto il podio.
 */
export function testoRisultatoArena(nome: string, dati: RiepilogoArena, t: TFunction): string {
  const righe = [
    nome,
    dati.posto ? `#${dati.posto} / ${dati.suQuante}` : '',
    `${dati.punti} ${t('tournament.lobby.pts')} | ${dati.partite} ${t('arena.results.games')}`,
    `${dati.percentuale}% ${t('arena.results.winRate')}`,
  ].filter(Boolean);

  const podio = righeDelPodio(dati.podio, t);
  if (podio.length > 0) {
    righe.push('', t('tournament.standings.title', { defaultValue: 'Standings' }), ...podio);
  }
  return righe.join('\n');
}

/**
 * Condivide la classifica di un torneo finito. La pagina del torneo e' pubblica
 * (`GET /api/tournaments/:id` risponde anche senza sessione), quindi il link si
 * apre da non registrati: e' cio' che rende sensato mandarlo in una chat.
 */
export async function condividiClassificaTorneo(
  id: string,
  nome: string,
  podio: RigaPodio[],
  t: TFunction,
): Promise<ShareResult> {
  const url = garaUrl('tournament', id);
  const text = testoClassificaTorneo(nome, podio, t);
  return condividi({
    title: t('tournament.share.title', { defaultValue: 'Draughts tournament' }),
    text,
    url,
    appunti: `${text}\n${url}`,
  });
}

/** La gemella per l'arena: il proprio risultato, il podio, e il link all'arena. */
export async function condividiRisultatoArena(
  id: string,
  nome: string,
  dati: RiepilogoArena,
  t: TFunction,
): Promise<ShareResult> {
  const url = garaUrl('arena', id);
  const text = testoRisultatoArena(nome, dati, t);
  return condividi({
    title: t('arena.share.title', { defaultValue: 'Draughts arena' }),
    text,
    url,
    appunti: `${text}\n${url}`,
  });
}
